export interface ParsedQuery {
  intent: string;
  confidence: number;
  parameters: Record<string, any>;
  entities: Array<{
    type: string;
    value: string;
    confidence: number;
  }>;
}

export class NaturalLanguageProcessor {
  private patterns: Map<string, RegExp[]> = new Map();

  constructor() {
    this.initializePatterns();
  }

  private initializePatterns() {
    // Cash Position Patterns
    this.patterns.set('cash-position', [
      /what('s| is) (my |our )?cash (position|balance)/i,
      /how much (money|cash) do (we|i) have/i,
      /show (me )?(the )?cash flow/i,
      /cash forecast/i,
      /current balance/i,
      /available funds/i
    ]);

    // Payment Acceleration Patterns
    this.patterns.set('payment-acceleration', [
      /which invoices (can|should) (i|we) accelerate/i,
      /get paid faster/i,
      /speed up (collections|payments)/i,
      /early payment discount/i,
      /accelerate (receivables|payments)/i,
      /faster cash collection/i,
      /payment acceleration/i
    ]);

    // Vendor Payment Patterns
    this.patterns.set('vendor-payments', [
      /optimize vendor payments/i,
      /which bills should (i|we) pay/i,
      /vendor payment schedule/i,
      /AP optimization/i,
      /payables optimization/i,
      /supplier payments/i,
      /bill payment strategy/i
    ]);

    // Contract Qualification Patterns
    this.patterns.set('contract-qualification', [
      /can (we|i) qualify for/i,
      /contract (qualification|readiness)/i,
      /financial strength/i,
      /am (i|we) qualified/i,
      /credit worthiness/i,
      /qualification status/i,
      /financial capacity/i
    ]);

    // Cross Border Patterns
    this.patterns.set('cross-border', [
      /cross[- ]border/i,
      /international (payments|vendors)/i,
      /FX optimization/i,
      /currency exposure/i,
      /foreign exchange/i,
      /international trade/i,
      /multi[- ]currency/i
    ]);
  }

  async parse(query: string): Promise<ParsedQuery> {
    const normalizedQuery = query.toLowerCase().trim();
    
    // Find the best matching intent
    let bestMatch = { intent: '', confidence: 0 };
    
    for (const [intent, patterns] of this.patterns) {
      for (const pattern of patterns) {
        if (pattern.test(normalizedQuery)) {
          const confidence = this.calculateConfidence(normalizedQuery, pattern);
          if (confidence > bestMatch.confidence) {
            bestMatch = { intent, confidence };
          }
        }
      }
    }

    // Extract entities and parameters
    const entities = this.extractEntities(normalizedQuery);
    const parameters = this.extractParameters(normalizedQuery, entities);

    return {
      intent: bestMatch.intent || 'unknown',
      confidence: bestMatch.confidence,
      parameters,
      entities
    };
  }

  private calculateConfidence(query: string, pattern: RegExp): number {
    const match = query.match(pattern);
    if (!match) return 0;
    
    // Base confidence based on match length vs query length
    const matchLength = match[0].length;
    const queryLength = query.length;
    let confidence = matchLength / queryLength;
    
    // Boost confidence for exact keyword matches
    const keywords = ['cash', 'payment', 'accelerate', 'vendor', 'qualify', 'cross-border'];
    const keywordMatches = keywords.filter(keyword => query.includes(keyword)).length;
    confidence += keywordMatches * 0.1;
    
    return Math.min(confidence, 1.0);
  }

  private extractEntities(query: string): Array<{ type: string; value: string; confidence: number }> {
    const entities = [];
    
    // Extract monetary amounts
    const moneyPattern = /\$?([\d,]+(?:\.\d{2})?)[kKmM]?/g;
    let match;
    while ((match = moneyPattern.exec(query)) !== null) {
      entities.push({
        type: 'amount',
        value: match[1],
        confidence: 0.9
      });
    }
    
    // Extract time periods
    const timePattern = /(\d+)\s*(day|week|month|year)s?/gi;
    while ((match = timePattern.exec(query)) !== null) {
      entities.push({
        type: 'timeframe',
        value: `${match[1]} ${match[2]}s`,
        confidence: 0.8
      });
    }
    
    // Extract currencies
    const currencyPattern = /(USD|EUR|GBP|CAD|JPY)/gi;
    while ((match = currencyPattern.exec(query)) !== null) {
      entities.push({
        type: 'currency',
        value: match[1].toUpperCase(),
        confidence: 0.95
      });
    }
    
    return entities;
  }

  private extractParameters(query: string, entities: Array<{ type: string; value: string; confidence: number }>): Record<string, any> {
    const parameters: Record<string, any> = {};
    
    // Convert entities to parameters
    entities.forEach(entity => {
      switch (entity.type) {
        case 'amount':
          parameters.amount = entity.value;
          break;
        case 'timeframe':
          parameters.timeframe = entity.value;
          break;
        case 'currency':
          parameters.currency = entity.value;
          break;
      }
    });
    
    // Extract action indicators
    if (query.includes('show') || query.includes('display') || query.includes('view')) {
      parameters.action = 'display';
    } else if (query.includes('optimize') || query.includes('improve')) {
      parameters.action = 'optimize';
    } else if (query.includes('analyze') || query.includes('check')) {
      parameters.action = 'analyze';
    }
    
    return parameters;
  }
}